import { LegalRight, BrandAccessRight, SeasonName } from 'helpers';
import { ChannelName } from 'src/app/content/services/marketing-files-search';
import { FcProducts } from 'types';
import { ContactType } from '../../../manufacturer-platform/brands/types';
import { FolderState } from 'app/content/types/category-types';
import { PriceLevel } from 'shared/services/brands.service';
import { OrderRestrictionType } from 'shared/services';
import { ImageShootType } from 'app/content/types/product-images';

export interface Brand {
    _id: string;
    name: string;
    glns: string[];
    logos: any[];
    phone?: string;
    organization?: string;
    description: string;
    fcProducts: FcProducts;
    showEndlessAisle: boolean;
    showReorder: boolean;
    showPreorder: boolean;
    isPremium: boolean;
    isFollowed: boolean;
    isNew: boolean;
    relations: Relations;
    termsOfUse: any;
    categories: string[];
    mainCategories: any; // TODO: use interface here
    priceLevel: PriceLevel[];
    website: string;
    ecommerceHint?: boolean;
    accessRequestCreated?: string;
    requestMarketingFiles?: { form: string; email: string };
    banner?: BrandBanner;
    retailerTermsOfUse?: string;
    contacts: BrandContacts;
    folders: Folder[];
    lookbooks: BrandAttachment[];
    sustainability: {
        website: string;
        certificates: string[];
        memberships: string[];
        reports: BrandAttachment[];
    };
    promoImage: any;
    manufacturer?: string;
    minimumOrderRestriction?: AmountOfItemsRestriction | OrderVolumeRestriction;
    address?: BrandAddress;
}

export type BrandAddress = {
    organizationName: string;
    street: string;
    city: string;
    zip: string;
    country: string;
};

interface BrandContacts {
    [ContactType.Main]: { email: ''; name: ''; preferredLanguage: '' };
    [ContactType.AccessRequests]: [];
    [ContactType.OrderConfirmation]: [];
    [ContactType.RetailerStoresUpdates]: [];
    [ContactType.Sustainability]?: { email: ''; name: ''; preferredLanguage: ''; phone?: '' };
}

export interface BrandAttachment {
    fileId: string;
    extension: string;
    name: string;
    size: number;
}

export interface Folder {
    _id?: string;
    name: string;
    image?: FolderCoverImage | null;
    state?: FolderState;
    oldName?: string;
}

export enum AttachmentTypes {
    Lookbooks = 'lookbooks',
    SustainabilityReports = 'sustainabilityReports'
}

export enum AttachmentContentTypes {
    S3Link = 's3Link',
    Base64String = 'base64String'
}

export interface AttachmentFile {
    name: string;
    size: number;
    metadata?: any;
    content: {
        source: AttachmentContentTypes;
        data: {
            filePath?: string;
            encodedString?: string;
        };
    };
}
export interface AttachmentRequestBody {
    type: AttachmentTypes;
    file: AttachmentFile;
}

export type FolderCoverImage = {
    _id: string;
    extension: string;
    size: string;
    content?: string;
    name?: string;
};

export interface BrandBanner {
    fileId: string;
    extension: string;
    size: number;
    updated: string;
    _id: string;
}

export type BrandForPreorderArea = Pick<Brand, '_id' | 'name'>;

export enum AccessState {
    NotRequested = 'NotRequested',
    Pending = 'Pending',
    Rejected = 'Rejected',
    Granted = 'Granted'
}

export interface Relations {
    [BrandAccessRight.MarketingMaterial]: AccessState;
    [BrandAccessRight.ProductData]: AccessState;
    [BrandAccessRight.EndlessAisle]: AccessState;
    [BrandAccessRight.ReorderPlatform]: AccessState;
    [BrandAccessRight.PreorderPlatform]: AccessState;
}

export interface BrandFromGetAll {
    _id: string;
    name: string;
    logo: string;
    gln: string;
    categories: string[];
    isPremium: boolean;
    fcProducts: FcProducts;
    hasMarketingFiles: boolean;
    hasProducts: boolean;
    contacts: BrandContacts;
}

export type FeaturedBrand = Partial<Brand> & {
    order: number;
    source: string;
};

export type LegalRightsMap = {
    [K in LegalRight]: K extends LegalRight.AutoRestock
        ? {
              value: boolean;
              access: string;
          }
        : boolean;
};

export interface Right {
    name: LegalRight;
    translationKey: string;
    selected: boolean;
    access?: string; // needed for smart replenishment
}

export interface RetailerAndRights {
    _id: string;
    name: string;
    simplifiedName: string;
    address: {
        line1: string;
        line2?: string;
        zip: string;
        city: string;
        country: string;
        countryCode: string;
    };
    phone?: string;
    categorizedRelations: { brandId: string; brandName: string; categorizedLegalRights: CategorizedLegalRights }[];
    relations: {
        [brandId: string]: {
            legalRights: LegalRightsMap;
            restrictions: {
                shootType: ImageShootType[];
            };
            termsOfUse?: string;
            termsOfUseChecked?: boolean;
        };
    };
    isEditing: boolean;
}

export interface CategorizedLegalRights {
    content: {
        product: Right[];
        marketing: Right[];
    };
    order: Right[];
}

interface MarketingFileData {
    _id: string;
    file: {
        _id: string;
        extension: string;
        size: string;
        height: number;
        width: number;
        colorModel: string;
        dpiHeight: number;
        dpiWidth: number;
    };
    public: boolean;
    created: string;
    versionTag: string;
    processedVideo: {
        thumbnails: Thumbnail[];
        extension: string;
    };
}

interface Thumbnail {
    extension: string;
    filename: string;
    _id: string;
}

export interface MarketingFileMetadata {
    channels: ChannelName[];
    expirationDate: string;
    season: SeasonName;
    tags: string[];
    articleNumbers: string[];
    year: number | null;
    category?: string;
    validFromDate: string;
    description: string;
    exclusiveRetailerGroups?: any[];
    exclusiveRetailerGroupIds?: string[];
}

export interface MarketingFile {
    _id: string;
    updated: string;
    created: string;
    files: MarketingFileData[];
    metadata: MarketingFileMetadata;
    requestMarketingFile: boolean;
    processedVideo?: {
        thumbnails: {
            filename: string;
            extension: string;
        }[];
    };
}

export enum NotificationTypes {
    AccessRequests = 'accessRequests',
    OrderConfirmation = 'orderConfirmation',
    RetailerStoresUpdates = 'retailerStoresUpdates'
}

export enum NotificationActions {
    Edited = 'edited',
    Added = 'added',
    Removed = 'removed'
}

export interface NotificationRequestBody {
    contactName: string;
    contactEmail: string;
    preferredLanguage: string;
    contactPhone?: string;
    relevantCountries?: string[];
    data: NotificationRequestData[];
}

export interface NotificationRequestData {
    id?: string;
    brandId: string;
    type: NotificationTypes;
    action?: NotificationActions;
}

export interface AmountOfItemsRestriction {
    type: OrderRestrictionType.AmountOfItems;
    parameter: number;
}

export interface OrderVolumeRestriction {
    type: OrderRestrictionType.OrderVolume;
    parameter: {
        [currency: string]: number;
    };
}
export interface RelationRetailer {
    _id: string;
    address: {
        city: string;
        country: string;
        line1: string;
        line2: string;
        zip: string;
    };
    name: string;
    relations: {
        [key: string]: string;
    };
}
